// ─── MarketplaceDataAudit.jsx ────────────────────────────────────────────────
// Debug / correctness-verification page for a single connected marketplace.
//
// Every number displayed is accompanied by (a) its UI-language label,
// (b) where it came from, and (c) the value. The page is pure read-only —
// no writes, no syncs triggered from here. Its job is to let the user
// trace "the realized payout is $X" back to the ingested Shopify fields
// and spot where the pipeline is wrong.
//
// Six sections:
//   1. Raw API data (what the sync ingested for the window)
//   2. Per-product breakdown (units, revenue, refunds, share)
//   3. Realized-payout cascade (tier + step-by-step allocation)
//   4. Share recompute inputs (why each product has its current %)
//   5. Downstream KPI traceback (which KPIs this marketplace feeds into)
//   6. Data-integrity warnings (automatic red flags)

import { useState, useMemo, useEffect } from "react";
import { useTheme } from "../lib/ThemeContext.jsx";
import { FONT, RADIUS } from "../lib/theme.js";
import { LS } from "../lib/storage.js";
import { currSym } from "../lib/calculations.js";
import { SALES_WINDOWS, DEFAULT_SALES_WINDOW } from "./marketplaces/marketplaceUtils.js";
import {
  realizedPayoutPerProduct, realizedPayoutForMkt,
  totalPayoutForMktInWindow, lineItemsInWindow, listPayoutsForMktInWindow,
  integrityChecks,
} from "../lib/calculations.js";

const fmtMoney = (n) => `${currSym()}${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtInt = (n) => (Number(n) || 0).toLocaleString();
const fmtPct = (n) => `${((Number(n) || 0) * 100).toFixed(2)}%`;
const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

// Row: label on left, value on right, optional source/formula hint below value.
function Row({ label, value, hint, indent = 0, strong = false, T }) {
  return (
    <div style={{
      display: "grid", gridTemplateColumns: "1fr auto",
      alignItems: "baseline", gap: 16,
      padding: "6px 0",
      borderBottom: `1px solid ${T.border}22`,
      paddingLeft: indent * 16,
    }}>
      <div style={{ fontSize: 13, color: strong ? T.text : T.textSub, fontWeight: strong ? 700 : 400 }}>
        {label}
        {hint && <div style={{ fontSize: 10, color: T.textMuted, fontStyle: "italic", marginTop: 2 }}>{hint}</div>}
      </div>
      <div style={{ fontSize: 13, color: T.text, fontWeight: strong ? 700 : 500, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
        {value}
      </div>
    </div>
  );
}

function SectionCard({ title, subtitle, children, T, accent }) {
  return (
    <div style={{
      background: T.surface, border: `1px solid ${T.border}`,
      borderRadius: RADIUS.md, padding: "16px 18px", marginBottom: 14,
    }}>
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: T.text, display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ width: 4, height: 14, background: accent || T.accent, borderRadius: 2 }} />
          {title}
        </div>
        {subtitle && <div style={{ fontSize: 11, color: T.textSub, marginTop: 4, marginLeft: 12 }}>{subtitle}</div>}
      </div>
      {children}
    </div>
  );
}

export default function MarketplaceDataAudit({ marketplaces = [], months = {}, hashQuery = {} }) {
  const T = useTheme();
  const connectedMkts = marketplaces.filter(m => m.apiConnection?.connected);
  // Preselect from ?mkt=<id> query param, else first connected, else first at all.
  const [selectedMktId, setSelectedMktId] = useState(() => {
    const queried = hashQuery?.mkt && marketplaces.some(m => m.id === hashQuery.mkt) ? hashQuery.mkt : null;
    return queried || connectedMkts[0]?.id || marketplaces[0]?.id || "";
  });
  // Shared LS key with Marketplaces page so flipping the window on either
  // view is remembered the next time either page opens.
  const [windowDays, setWindowDays] = useState(() => {
    const v = parseInt(LS.get?.("sf_mkt_sales_window", "") || "", 10);
    return Number.isFinite(v) && v > 0 ? v : DEFAULT_SALES_WINDOW;
  });
  useEffect(() => { try { LS.set?.("sf_mkt_sales_window", String(windowDays)); } catch {} }, [windowDays]);

  const mkt = marketplaces.find(m => m.id === selectedMktId) || null;
  const state = useMemo(() => ({ months }), [months]);

  // Derived: everything the page renders, computed once per (mkt, months, windowDays).
  const audit = useMemo(() => {
    if (!mkt) return null;
    const agg = mkt.apiConnection?.windowAgg?.[windowDays] || null;
    const payoutsList = listPayoutsForMktInWindow(mkt, state, windowDays);
    const payoutTotal = totalPayoutForMktInWindow(mkt, state, windowDays);
    const lineItems = lineItemsInWindow(mkt, windowDays);
    const perProduct = realizedPayoutPerProduct(mkt, state, windowDays);
    const blendedRealized = realizedPayoutForMkt(mkt, state, windowDays);
    const blendedList = (mkt.products || []).reduce((s, p) => s + (Number(p.pctOfMarket) || 0) * (Number(p.payout) || 0), 0);
    const warnings = integrityChecks(mkt, state, windowDays);

    // Σ per-product values (the audit page derives these so they can be
    // compared against the stored scalars for drift detection).
    const products = mkt.products || [];
    const perProductStats = products.map(p => {
      const w = p?._salesWindows?.[windowDays] || p?._salesWindows?.[String(windowDays)] || {};
      return {
        p,
        units: Number(w.units) || 0,
        lineItems: Number(w.lineItems) || 0,
        revenue: Number(w.revenue) || 0,
        refunds: Number(w.refunds) || 0,
        netRevenue: Math.max(0, (Number(w.revenue) || 0) - (Number(w.refunds) || 0)),
        storedShare: Number(p.pctOfMarket) || 0,
        realized: perProduct.get(p.id) || null,
      };
    });
    const totals = perProductStats.reduce((acc, s) => ({
      units: acc.units + s.units,
      lineItems: acc.lineItems + s.lineItems,
      revenue: acc.revenue + s.revenue,
      refunds: acc.refunds + s.refunds,
      netRevenue: acc.netRevenue + s.netRevenue,
    }), { units: 0, lineItems: 0, revenue: 0, refunds: 0, netRevenue: 0 });

    // Recomputed share = units share of total (matches recomputeProductShares for non-overridden products).
    const recomputedShares = new Map();
    if (totals.units > 0) {
      for (const s of perProductStats) {
        recomputedShares.set(s.p.id, s.units / totals.units);
      }
    }

    // Tier determination (mirrors realizedPayoutPerProduct's logic).
    let tier = "list_price";
    let tierReason = "";
    if (payoutTotal > 0 && (totals.netRevenue > 0 || totals.units > 0)) {
      tier = "realized";
      tierReason = `Payouts in window = ${fmtMoney(payoutTotal)} (>0) AND Σ net revenue = ${fmtMoney(totals.netRevenue)} (>0).`;
    } else if (totals.netRevenue > 0) {
      tier = "net_revenue";
      tierReason = `No payouts in window yet. Falling back to (revenue − refunds) ÷ units per product.`;
    } else {
      tier = "list_price";
      tierReason = `No order history in window. Using the stored list price on each product.`;
    }

    return { agg, payoutsList, payoutTotal, lineItems, perProductStats, totals, recomputedShares, blendedRealized, blendedList, tier, tierReason, warnings };
  }, [mkt, state, windowDays]);

  const copyToClipboard = async () => {
    if (!mkt || !audit) return;
    const lines = [];
    lines.push(`CashHorizon · Marketplace Data Audit · ${mkt.name} · ${windowDays}-day window`);
    lines.push(`Generated: ${new Date().toLocaleString()}`);
    lines.push("");
    lines.push("SECTION 1 · Raw API data");
    lines.push(`  Orders received in window:     ${fmtInt(audit.agg?.orders ?? mkt.ordersCount ?? 0)}`);
    lines.push(`  Units sold (total quantity):   ${fmtInt(audit.agg?.units ?? mkt.unitsCount ?? 0)}`);
    lines.push(`  Products ordered (line items): ${fmtInt(audit.lineItems)}`);
    lines.push(`  Gross revenue:                 ${fmtMoney(audit.agg?.gross ?? audit.totals.revenue)}`);
    lines.push(`    − Customer discounts:        ${fmtMoney(audit.agg?.discounts ?? 0)}`);
    lines.push(`    − Tax collected (liability): ${fmtMoney(audit.agg?.tax ?? 0)}`);
    lines.push(`    − Shipping charged:          ${fmtMoney(audit.agg?.shipping ?? 0)}`);
    lines.push(`  Refunds issued:                ${fmtMoney(audit.agg?.refunds ?? audit.totals.refunds)}`);
    lines.push(`  Total payouts received:        ${fmtMoney(audit.payoutTotal)}  (${audit.payoutsList.length} payouts)`);
    lines.push(`  Reserve held:                  ${fmtMoney(mkt.apiConnection?.reservesHeld || 0)}`);
    lines.push(`  Synced inventory:              ${fmtInt(mkt.lastSyncStock || 0)} units`);
    lines.push(`  Last sync:                     ${fmtDate(mkt.apiConnection?.lastSync)}`);
    lines.push("");
    lines.push("SECTION 3 · Realized-payout cascade");
    lines.push(`  Selected tier: ${audit.tier}`);
    lines.push(`  Reason: ${audit.tierReason}`);
    lines.push(`  Blended list-price $/unit:     ${fmtMoney(audit.blendedList)}`);
    lines.push(`  Blended realized $/unit:       ${fmtMoney(audit.blendedRealized)}`);
    lines.push("");
    if (audit.warnings.length) {
      lines.push(`SECTION 6 · Warnings (${audit.warnings.length})`);
      for (const w of audit.warnings) lines.push(`  ⚠ ${w.label} — ${w.detail}`);
    }
    try {
      await navigator.clipboard.writeText(lines.join("\n"));
    } catch {}
  };

  if (!marketplaces.length) {
    return (
      <div style={{ fontFamily: FONT, padding: 24, color: T.textSub }}>
        <h2 style={{ color: T.text }}>Marketplace Data Audit</h2>
        <p>No marketplaces yet. Add a marketplace and connect it to an API to see data here.</p>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: FONT, padding: "16px 20px", maxWidth: 1100, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 16, flexWrap: "wrap" }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: T.text, margin: 0 }}>Marketplace Data Audit</h2>
        <span style={{ fontSize: 11, color: T.textSub, background: T.surface, padding: "3px 9px", borderRadius: RADIUS.pill, border: `1px solid ${T.border}` }}>
          Read-only · Verifies sync + calc correctness
        </span>
      </div>

      {/* Marketplace + window controls */}
      <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 11, color: T.textSub, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.3 }}>Marketplace</span>
          <select value={selectedMktId} onChange={e => setSelectedMktId(e.target.value)}
            style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: RADIUS.sm, color: T.text, fontSize: 13, fontFamily: FONT, padding: "5px 10px", outline: "none", cursor: "pointer", minWidth: 180 }}>
            {marketplaces.map(m => (
              <option key={m.id} value={m.id}>
                {m.name}{m.apiConnection?.connected ? " · connected" : ""}
              </option>
            ))}
          </select>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 11, color: T.textSub, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.3 }}>Window</span>
          <select value={windowDays} onChange={e => setWindowDays(parseInt(e.target.value, 10))}
            style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: RADIUS.sm, color: T.text, fontSize: 13, fontFamily: FONT, padding: "5px 10px", outline: "none", cursor: "pointer" }}>
            {SALES_WINDOWS.map(w => <option key={w.days} value={w.days}>{w.label}</option>)}
          </select>
        </div>
        <div style={{ flex: 1 }} />
        <button onClick={copyToClipboard}
          style={{ padding: "5px 14px", borderRadius: RADIUS.sm, fontSize: 12, fontFamily: FONT, fontWeight: 600, cursor: "pointer", background: T.accent + "18", border: `1px solid ${T.accent}33`, color: T.accent }}>
          Copy all as text
        </button>
      </div>

      {!mkt && <div style={{ color: T.textSub }}>Select a marketplace.</div>}
      {mkt && !mkt.apiConnection?.connected && (
        <div style={{ padding: "10px 14px", background: T.orange + "15", border: `1px solid ${T.orange}44`, borderRadius: RADIUS.md, color: T.orange, fontSize: 12, marginBottom: 14 }}>
          This marketplace has no API connection. The audit below shows whatever's in memory, which will mostly be empty.
        </div>
      )}

      {/* Integrity warnings (Section 6 — hoisted to top for visibility) */}
      {mkt && audit && audit.warnings.length > 0 && (
        <SectionCard title={`⚠ Data-integrity warnings (${audit.warnings.length})`} T={T} accent={T.orange}
          subtitle="Automatic sanity checks. Each item tells you what's off and usually how to fix it.">
          {audit.warnings.map((w, i) => (
            <div key={i} style={{ padding: "10px 12px", background: T.orange + "0a", borderLeft: `3px solid ${T.orange}`, borderRadius: RADIUS.sm, marginBottom: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: T.orange, marginBottom: 4 }}>{w.label}</div>
              <div style={{ fontSize: 12, color: T.textSub, lineHeight: 1.5 }}>{w.detail}</div>
            </div>
          ))}
        </SectionCard>
      )}

      {/* Section 1 — Raw API data */}
      {mkt && audit && (
        <SectionCard title="1. Raw API data" T={T}
          subtitle={`What the sync ingested for the ${windowDays}-day window. Every row maps to a specific field in the Shopify Admin API response.`}>
          <Row T={T} label="Orders received in window" value={fmtInt(audit.agg?.orders ?? mkt.ordersCount ?? 0)}
            hint="Distinct orders. Voided / fully-refunded / cancelled orders are skipped." />
          <Row T={T} label="Units sold (total quantity)" value={fmtInt(audit.agg?.units ?? mkt.unitsCount ?? 0)}
            hint="Σ line_item.quantity across all orders in the window." />
          <Row T={T} label="Products ordered (line items)" value={fmtInt(audit.lineItems)}
            hint="Σ number of distinct product rows across orders. An order with 3 line items counts as 3." />
          <Row T={T} strong label="Gross revenue (before deductions)" value={fmtMoney(audit.agg?.gross ?? audit.totals.revenue)}
            hint="Σ order.total_price. Includes discounts + tax + shipping — see rows below." />
          <Row T={T} indent={1} label="− Customer discounts" value={`−${fmtMoney(audit.agg?.discounts ?? 0)}`} />
          <Row T={T} indent={1} label="− Tax collected (liability, not income)" value={`−${fmtMoney(audit.agg?.tax ?? 0)}`}
            hint="The seller owes this to tax authorities; it's not earnings." />
          <Row T={T} indent={1} label="− Shipping charged (pass-through)" value={`−${fmtMoney(audit.agg?.shipping ?? 0)}`} />
          <Row T={T} strong label="= Product revenue (estimated net)" value={fmtMoney(Math.max(0, (audit.agg?.gross ?? 0) - (audit.agg?.discounts ?? 0) - (audit.agg?.tax ?? 0) - (audit.agg?.shipping ?? 0)))}
            hint="Gross − discounts − tax − shipping. Rough; Shopify returns these at the order grain so the deduction is approximate vs. processing-fee basis." />
          <Row T={T} label="Refunds issued" value={fmtMoney(audit.agg?.refunds ?? audit.totals.refunds)}
            hint="Σ of refunds[].transactions[].amount across orders." />
          <Row T={T} strong label="Total payouts received (net cash)" value={fmtMoney(audit.payoutTotal)}
            hint={`Σ Shopify Payments payouts in [now − ${windowDays}d − 3d lag … now]. ${audit.payoutsList.length} payout(s).`} />
          <Row T={T} indent={1} label="− Shopify processing fees (all pulled payouts)" value={`−${fmtMoney(mkt.apiConnection?.feeTotals?.charges || 0)}`}
            hint="summary.charges_fee_amount summed across last 50 payouts. Not scoped to window — aggregate." />
          <Row T={T} indent={1} label="− Refund fees" value={`−${fmtMoney(mkt.apiConnection?.feeTotals?.refunds || 0)}`} />
          <Row T={T} label="Channel mix (orders)" value={`${fmtInt(audit.agg?.web ?? 0)} web · ${fmtInt(audit.agg?.pos ?? 0)} POS`} />
          {(() => {
            const cb = mkt.apiConnection?.channelBreakdown?.[windowDays] || {};
            const rows = Object.entries(cb).sort((a, b) => (b[1].units || 0) - (a[1].units || 0));
            if (rows.length === 0) return null;
            const include = Array.isArray(mkt.apiConnection?.includeChannels) ? mkt.apiConnection.includeChannels : null;
            return (
              <div style={{ padding: "8px 0", borderBottom: `1px solid ${T.border}22` }}>
                <div style={{ fontSize: 13, color: T.textSub, marginBottom: 6 }}>Per-channel breakdown <span style={{ fontSize: 10, color: T.textMuted, fontStyle: "italic" }}>— source_name field on each order</span></div>
                {rows.map(([src, s]) => {
                  const excluded = include && !include.includes(src);
                  return (
                    <div key={src} style={{ display:"grid", gridTemplateColumns:"1fr auto auto auto", gap:12, alignItems:"baseline", padding:"4px 0 4px 16px", fontSize:12 }}>
                      <div style={{ color: excluded ? T.textMuted : T.text, fontWeight:600, fontFamily:"monospace" }}>
                        {src}{excluded ? " (excluded from aggregates)" : ""}
                      </div>
                      <div style={{ color:T.textSub, fontVariantNumeric:"tabular-nums" }}>{fmtInt(s.orders || 0)} orders</div>
                      <div style={{ color:T.textSub, fontVariantNumeric:"tabular-nums" }}>{fmtInt(s.units || 0)} units</div>
                      <div style={{ color:T.text, fontVariantNumeric:"tabular-nums", minWidth:90, textAlign:"right" }}>{fmtMoney(s.revenue || 0)}</div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
          <Row T={T} label="Cancelled orders (skipped)" value={fmtInt(audit.agg?.cancelled ?? 0)}
            hint="Orders with cancelled_at set — excluded from every aggregate above." />
          <Row T={T} label="Reserve held" value={fmtMoney(mkt.apiConnection?.reservesHeld || 0)} />
          <Row T={T} label="Synced inventory" value={`${fmtInt(mkt.lastSyncStock || 0)} units`} />
          <Row T={T} label="Last successful sync" value={fmtDate(mkt.apiConnection?.lastSync)} />
          <Row T={T} label="Window end timestamp" value={fmtDate(mkt.apiConnection?.windowEnd)} />
        </SectionCard>
      )}

      {/* Section 2 — Per-product table */}
      {mkt && audit && audit.perProductStats.length > 0 && (
        <SectionCard title={`2. Per-product breakdown (${audit.perProductStats.length} products)`} T={T}
          subtitle="One row per product, with the raw numbers and the derived share / realized payout side-by-side.">
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse", fontVariantNumeric: "tabular-nums" }}>
              <thead>
                <tr style={{ color: T.textSub, textAlign: "right" }}>
                  <th style={{ textAlign: "left", padding: "6px 8px", fontWeight: 600, borderBottom: `1px solid ${T.border}` }}>Product</th>
                  <th style={{ padding: "6px 8px", fontWeight: 600, borderBottom: `1px solid ${T.border}` }}>SKU</th>
                  <th style={{ padding: "6px 8px", fontWeight: 600, borderBottom: `1px solid ${T.border}` }}>Stock</th>
                  <th style={{ padding: "6px 8px", fontWeight: 600, borderBottom: `1px solid ${T.border}` }}>Units</th>
                  <th style={{ padding: "6px 8px", fontWeight: 600, borderBottom: `1px solid ${T.border}` }}>Line items</th>
                  <th style={{ padding: "6px 8px", fontWeight: 600, borderBottom: `1px solid ${T.border}` }}>Revenue</th>
                  <th style={{ padding: "6px 8px", fontWeight: 600, borderBottom: `1px solid ${T.border}` }}>Refunds</th>
                  <th style={{ padding: "6px 8px", fontWeight: 600, borderBottom: `1px solid ${T.border}` }}>Net rev.</th>
                  <th style={{ padding: "6px 8px", fontWeight: 600, borderBottom: `1px solid ${T.border}` }} title="List price per unit (product.payout)">List $/u</th>
                  <th style={{ padding: "6px 8px", fontWeight: 600, borderBottom: `1px solid ${T.border}` }} title="Σ productCost + shipping + customs + prep from product.cogs">COGS/u</th>
                  <th style={{ padding: "6px 8px", fontWeight: 600, borderBottom: `1px solid ${T.border}` }} title="Advertising cost per unit. Product setting falls back to marketplace/group default.">Ad/u</th>
                  <th style={{ padding: "6px 8px", fontWeight: 600, borderBottom: `1px solid ${T.border}` }} title="User-entered refund deduction (distinct from API refund transactions in the Refunds column).">Refund/u</th>
                  <th style={{ padding: "6px 8px", fontWeight: 600, borderBottom: `1px solid ${T.border}` }} title="product.deliveryCost">Delivery/u</th>
                  <th style={{ padding: "6px 8px", fontWeight: 600, borderBottom: `1px solid ${T.border}` }} title="List $/u − Ad − Refund − Delivery − COGS. The seller's take-home per unit sold.">Net $/u</th>
                  <th style={{ padding: "6px 8px", fontWeight: 600, borderBottom: `1px solid ${T.border}` }} title="Net $/u ÷ List $/u. Below 0% means the product loses money on every unit.">Margin</th>
                  <th style={{ padding: "6px 8px", fontWeight: 600, borderBottom: `1px solid ${T.border}` }}>Stored %</th>
                  <th style={{ padding: "6px 8px", fontWeight: 600, borderBottom: `1px solid ${T.border}` }}>Recomp %</th>
                  <th style={{ padding: "6px 8px", fontWeight: 600, borderBottom: `1px solid ${T.border}` }}>Realized $/u</th>
                  <th style={{ padding: "6px 8px", fontWeight: 600, borderBottom: `1px solid ${T.border}` }}>Basis</th>
                  <th style={{ padding: "6px 8px", fontWeight: 600, borderBottom: `1px solid ${T.border}` }} title="Decoupled products are excluded from every KPI and share calc.">Flag</th>
                </tr>
              </thead>
              <tbody>
                {audit.perProductStats
                  .slice()
                  .sort((a, b) => b.units - a.units)
                  .map(s => {
                    const recomp = audit.recomputedShares.get(s.p.id) ?? 0;
                    const drift = Math.abs(s.storedShare - recomp) > 0.005;
                    const apiStock = s.p.apiStock;
                    const manualStock = s.p.currentStock;
                    const stockVal = apiStock != null ? apiStock : (manualStock != null ? manualStock : null);
                    const stockSource = apiStock != null ? "api" : (manualStock != null ? "manual" : null);
                    // Per-unit cost breakdown. COGS is the 4-field sum; Ad
                    // falls back to marketplace/group default when the
                    // product doesn't override; Refund is user-entered
                    // (distinct from the API refunds column above).
                    const listPrice = Number(s.p.payout) || 0;
                    const cogs = s.p.cogs || {};
                    const cogsPerUnit = (Number(cogs.productCost) || 0) + (Number(cogs.shipping) || 0) + (Number(cogs.customs) || 0) + (Number(cogs.prep) || 0);
                    const adType = s.p.adCostType || (mkt.adCostType || "pct");
                    const adRate = Number(s.p.adCostRate ?? mkt.adCostRate) || 0;
                    const adAmount = Number(s.p.adCostAmount ?? mkt.adCostAmount) || 0;
                    const adPerUnit = listPrice > 0 ? (adType === "pct" ? listPrice * adRate : adAmount) : 0;
                    const refundType = s.p.refundType || "pct";
                    const refundRate = Number(s.p.refundRate) || 0;
                    const refundAmount = Number(s.p.refundAmount) || 0;
                    const refundPerUnit = listPrice > 0 && refundType === "pct" ? listPrice * refundRate : refundAmount;
                    const deliveryPerUnit = Number(s.p.deliveryCost) || 0;
                    const netPerUnit = listPrice - adPerUnit - refundPerUnit - deliveryPerUnit - cogsPerUnit;
                    const marginPct = listPrice > 0 ? netPerUnit / listPrice : 0;
                    const fullName = s.p.name || "Unnamed";
                    const firstWord = fullName.split(/\s+/)[0] || fullName;
                    return (
                      <tr key={s.p.id} style={{ color: T.text, opacity: s.p._decoupled ? 0.55 : 1 }}>
                        <td title={fullName} style={{ padding: "5px 8px", maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "help" }}>{firstWord}</td>
                        <td style={{ padding: "5px 8px", textAlign: "right", color: T.textSub, fontFamily: "monospace", fontSize: 11 }}>{s.p.sku || "—"}</td>
                        <td style={{ padding: "5px 8px", textAlign: "right", color: stockVal == null ? T.textMuted : (stockVal === 0 ? T.red : T.text) }} title={stockSource === "api" ? "From API sync" : stockSource === "manual" ? "Manually entered" : "No stock info"}>
                          {stockVal != null ? fmtInt(stockVal) : "—"}
                        </td>
                        <td style={{ padding: "5px 8px", textAlign: "right" }}>{fmtInt(s.units)}</td>
                        <td style={{ padding: "5px 8px", textAlign: "right" }}>{fmtInt(s.lineItems)}</td>
                        <td style={{ padding: "5px 8px", textAlign: "right" }}>{fmtMoney(s.revenue)}</td>
                        <td style={{ padding: "5px 8px", textAlign: "right", color: s.refunds > 0 ? T.red : T.textSub }}>{fmtMoney(s.refunds)}</td>
                        <td style={{ padding: "5px 8px", textAlign: "right" }}>{fmtMoney(s.netRevenue)}</td>
                        <td style={{ padding: "5px 8px", textAlign: "right" }}>{fmtMoney(listPrice)}</td>
                        <td style={{ padding: "5px 8px", textAlign: "right", color: cogsPerUnit > 0 ? T.text : T.textMuted }}>{cogsPerUnit > 0 ? fmtMoney(cogsPerUnit) : "—"}</td>
                        <td style={{ padding: "5px 8px", textAlign: "right", color: adPerUnit > 0 ? T.text : T.textMuted }}>{adPerUnit > 0 ? fmtMoney(adPerUnit) : "—"}</td>
                        <td style={{ padding: "5px 8px", textAlign: "right", color: refundPerUnit > 0 ? T.text : T.textMuted }}>{refundPerUnit > 0 ? fmtMoney(refundPerUnit) : "—"}</td>
                        <td style={{ padding: "5px 8px", textAlign: "right", color: deliveryPerUnit > 0 ? T.text : T.textMuted }}>{deliveryPerUnit > 0 ? fmtMoney(deliveryPerUnit) : "—"}</td>
                        <td style={{ padding: "5px 8px", textAlign: "right", fontWeight: 700, color: netPerUnit < 0 ? T.red : (netPerUnit < listPrice * 0.2 ? T.orange : T.green) }}>{fmtMoney(netPerUnit)}</td>
                        <td style={{ padding: "5px 8px", textAlign: "right", color: marginPct < 0 ? T.red : (marginPct < 0.2 ? T.orange : T.green) }}>{listPrice > 0 ? `${(marginPct * 100).toFixed(1)}%` : "—"}</td>
                        <td style={{ padding: "5px 8px", textAlign: "right" }}>{fmtPct(s.storedShare)}</td>
                        <td style={{ padding: "5px 8px", textAlign: "right", color: drift ? T.orange : T.textSub }}>{fmtPct(recomp)}</td>
                        <td style={{ padding: "5px 8px", textAlign: "right" }}>{s.realized ? fmtMoney(s.realized.perUnit) : "—"}</td>
                        <td style={{ padding: "5px 8px", textAlign: "right", fontSize: 10, color: T.textSub }}>{s.realized?.basis || "—"}</td>
                        <td style={{ padding: "5px 8px", textAlign: "right" }}>
                          {s.p._decoupled
                            ? <span style={{ fontSize: 9, color: T.red, fontWeight: 700, padding: "1px 6px", borderRadius: 8, background: T.red + "15", border: `1px solid ${T.red}40` }}>DECOUPLED</span>
                            : <span style={{ color: T.textMuted }}>—</span>
                          }
                        </td>
                      </tr>
                    );
                  })}
                {/* Totals */}
                {(() => {
                  const totalCogs = audit.perProductStats.reduce((sum, x) => {
                    const c = x.p.cogs || {};
                    return sum + ((Number(c.productCost) || 0) + (Number(c.shipping) || 0) + (Number(c.customs) || 0) + (Number(c.prep) || 0)) * x.units;
                  }, 0);
                  const totalDelivery = audit.perProductStats.reduce((sum, x) => sum + (Number(x.p.deliveryCost) || 0) * x.units, 0);
                  return (
                    <tr style={{ color: T.text, fontWeight: 700, borderTop: `2px solid ${T.border}` }}>
                      <td style={{ padding: "8px", textAlign: "left" }}>Totals</td>
                      <td style={{ padding: "8px", textAlign: "right", color: T.textSub }}>—</td>
                      <td style={{ padding: "8px", textAlign: "right" }}>
                        {fmtInt(audit.perProductStats.reduce((s, x) => s + (Number(x.p.apiStock ?? x.p.currentStock) || 0), 0))}
                      </td>
                      <td style={{ padding: "8px", textAlign: "right" }}>{fmtInt(audit.totals.units)}</td>
                      <td style={{ padding: "8px", textAlign: "right" }}>{fmtInt(audit.totals.lineItems)}</td>
                      <td style={{ padding: "8px", textAlign: "right" }}>{fmtMoney(audit.totals.revenue)}</td>
                      <td style={{ padding: "8px", textAlign: "right" }}>{fmtMoney(audit.totals.refunds)}</td>
                      <td style={{ padding: "8px", textAlign: "right" }}>{fmtMoney(audit.totals.netRevenue)}</td>
                      <td colSpan={1} />
                      <td style={{ padding: "8px", textAlign: "right" }} title="Σ COGS/u × units">{totalCogs > 0 ? fmtMoney(totalCogs) : "—"}</td>
                      <td colSpan={2} />
                      <td style={{ padding: "8px", textAlign: "right" }} title="Σ Delivery/u × units">{totalDelivery > 0 ? fmtMoney(totalDelivery) : "—"}</td>
                      <td colSpan={7} />
                    </tr>
                  );
                })()}
              </tbody>
            </table>
          </div>
        </SectionCard>
      )}

      {/* Section 3 — Realized cascade */}
      {mkt && audit && (
        <SectionCard title="3. Realized-payout cascade" T={T} accent={T.green}
          subtitle="Which of the three tiers the calc picked for this marketplace, and why.">
          <Row T={T} strong label="Selected tier" value={audit.tier} />
          <Row T={T} label="Reason" value={""} hint={audit.tierReason} />
          <Row T={T} label="Blended list-price $/unit" value={fmtMoney(audit.blendedList)}
            hint="Σ pctOfMarket × product.payout (list price). What the old calc returned." />
          <Row T={T} strong label="Blended realized $/unit" value={fmtMoney(audit.blendedRealized)}
            hint="Σ pctOfMarket × realized per-unit. What every downstream KPI now uses." />
          <Row T={T} label="Delta (list − realized)" value={fmtMoney(audit.blendedList - audit.blendedRealized)}
            hint="Positive means realized is lower — usually processing fees. Most stores: 2-5%." />

          {audit.payoutsList.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, color: T.textSub, marginBottom: 6 }}>
                Payouts making up the {fmtMoney(audit.payoutTotal)} numerator
              </div>
              <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse", fontVariantNumeric: "tabular-nums" }}>
                <thead>
                  <tr style={{ color: T.textSub }}>
                    <th style={{ textAlign: "left", padding: "4px 8px", fontWeight: 600 }}>Date</th>
                    <th style={{ textAlign: "right", padding: "4px 8px", fontWeight: 600 }}>Net amount</th>
                    <th style={{ textAlign: "right", padding: "4px 8px", fontWeight: 600 }}>Processing fees</th>
                    <th style={{ textAlign: "right", padding: "4px 8px", fontWeight: 600 }}>Gross card volume</th>
                  </tr>
                </thead>
                <tbody>
                  {audit.payoutsList.map(pp => (
                    <tr key={pp.id} style={{ color: T.text, borderTop: `1px solid ${T.border}22` }}>
                      <td style={{ padding: "4px 8px" }}>{pp.date}</td>
                      <td style={{ padding: "4px 8px", textAlign: "right" }}>{fmtMoney(pp.amount)}</td>
                      <td style={{ padding: "4px 8px", textAlign: "right", color: T.textSub }}>{pp.chargesFee > 0 ? `−${fmtMoney(pp.chargesFee)}` : "—"}</td>
                      <td style={{ padding: "4px 8px", textAlign: "right", color: T.textSub }}>{pp.chargesGross > 0 ? fmtMoney(pp.chargesGross) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>
      )}

      {/* Section 4 — Share recompute inputs */}
      {mkt && audit && (
        <SectionCard title="4. Share recompute inputs" T={T}
          subtitle="The math behind each product's stored percentage. If stored % and recomputed % disagree, click Recompute on the Marketplaces page.">
          <Row T={T} strong label="Total volume (non-decoupled units)" value={fmtInt(audit.totals.units)}
            hint="Denominator for the auto-share calculation." />
          <Row T={T} label="Σ stored pctOfMarket" value={fmtPct((mkt.products || []).filter(p => !p._decoupled).reduce((s, p) => s + (Number(p.pctOfMarket) || 0), 0))}
            hint="Should equal 100.00%. If it doesn't, a manual override is off or Recompute hasn't run." />
          <Row T={T} label="Decoupled products (excluded from share)" value={fmtInt((mkt.products || []).filter(p => p._decoupled).length)} />
        </SectionCard>
      )}

      {/* Section 5 — Inventory */}
      {mkt && audit && (() => {
        const products = mkt.products || [];
        const apiStockSum = products.reduce((s, p) => s + (Number(p.apiStock) || 0), 0);
        const manualStockSum = products.reduce((s, p) => s + (Number(p.currentStock) || 0), 0);
        const productsWithApiStock = products.filter(p => p.apiStock != null).length;
        const productsWithoutStock = products.filter(p => !p.apiStock && !p.currentStock).length;
        // Rough pace-based runway using the 30-day window units.
        const totalUnits30 = products.reduce((s, p) => {
          const w = p?._salesWindows?.[30] || p?._salesWindows?.["30"];
          return s + (Number(w?.units) || 0);
        }, 0);
        const paceDaily = totalUnits30 / 30;
        const runwayDays = paceDaily > 0 ? Math.floor(apiStockSum / paceDaily) : null;
        return (
          <SectionCard title="5. Inventory" T={T}
            subtitle="Stock currently on hand for this marketplace and the estimated runway at today's pace.">
            <Row T={T} strong label="Synced inventory (marketplace total)" value={`${fmtInt(mkt.lastSyncStock ?? apiStockSum)} units`}
              hint="Σ variants[].inventory_quantity from the last products sync." />
            <Row T={T} label="Σ per-product apiStock" value={fmtInt(apiStockSum)}
              hint="Summed across the products in this marketplace. Ties out with the marketplace total above." />
            <Row T={T} label="Products with API stock" value={fmtInt(productsWithApiStock)}
              hint={`${productsWithApiStock} of ${products.length} products have a synced apiStock value.`} />
            {productsWithoutStock > 0 && (
              <Row T={T} label="Products with no stock info" value={fmtInt(productsWithoutStock)}
                hint="Manually added products or first-time syncs that haven't captured a stock snapshot yet." />
            )}
            {mkt.lastSyncStockDate && (
              <Row T={T} label="Inventory snapshot date" value={mkt.lastSyncStockDate} />
            )}
            <Row T={T} label="Current stock override" value={mkt.stockOverridden ? `${fmtInt(mkt.currentStockOverride || 0)} units (locked)` : "—"}
              hint="Sticky manual value. When set, it wins over the synced total at calc time." />
            {manualStockSum > 0 && (
              <Row T={T} label="Σ per-product manual stock" value={fmtInt(manualStockSum)}
                hint="Applied per-product only when there's no apiStock. Kept across syncs." />
            )}
            <Row T={T} label="Reserve held (money, not units)" value={fmtMoney(mkt.apiConnection?.reservesHeld || 0)} />
            <Row T={T} strong label="Estimated runway at 30-day pace" value={runwayDays != null ? `${fmtInt(runwayDays)} days` : "—"}
              hint={paceDaily > 0 ? `${paceDaily.toFixed(1)} units/day × inventory.` : "No recent sales, so runway can't be estimated."} />
          </SectionCard>
        );
      })()}

      {/* Section 6 — Downstream KPI traceback */}
      {mkt && audit && (
        <SectionCard title="6. Downstream KPI traceback" T={T}
          subtitle="This marketplace's contribution to the brand-wide numbers on the Dashboard.">
          <Row T={T} label="This marketplace's % of total sales" value={fmtPct(Number(mkt.pctOfTotal) || 0)} />
          <Row T={T} label="Blended realized $/unit (this mkt)" value={fmtMoney(audit.blendedRealized)} />
          <Row T={T} strong label="Contribution to brand blended payout" value={fmtMoney((Number(mkt.pctOfTotal) || 0) * audit.blendedRealized)}
            hint="mkt.pctOfTotal × blended realized — this is what the Dashboard's blended KPI sums across marketplaces." />
        </SectionCard>
      )}

      <div style={{ fontSize: 11, color: T.textMuted, textAlign: "center", marginTop: 20, marginBottom: 20 }}>
        All numbers are read directly from in-memory brand state (no fresh API calls). Re-sync from the Marketplaces page to refresh.
      </div>
    </div>
  );
}
